import { useEffect, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { INTAKE_SUBMISSION_STATUS } from '@/constants/intake';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useWorkspaceIntakeSubmissionsQuery, WORKSPACE_INTAKE_PAGE_SIZE } from '@/hooks/useIntake';
import { IntakeInboxTable } from './IntakeInboxTable';
import { IntakeStatusFilter } from './IntakeStatusFilter';

export function IntakeInboxPage() {
  const { t } = useTranslation('intake');
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(INTAKE_SUBMISSION_STATUS.submitted);

  useEffect(
    function resetPageOnFilterChange() {
      setPage(1);
    },
    [debouncedSearch, statusFilter]
  );

  const submissionsQuery = useWorkspaceIntakeSubmissionsQuery(page, debouncedSearch, statusFilter);
  const submissionsPage = submissionsQuery.data;

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader title={t('inbox.title')} subtitle={t('inbox.subtitle')} />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1">
          <ListSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('inbox.search')}
          />
        </div>
        <IntakeStatusFilter value={statusFilter} onChange={setStatusFilter} />
      </div>

      {submissionsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : submissionsQuery.isError === true || submissionsPage === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            submissionsQuery.refetch();
          }}
        />
      ) : (
        <>
          <IntakeInboxTable
            submissions={submissionsPage.items}
            hasFilters={debouncedSearch.trim().length > 0}
            onOpenSubmission={function openSubmission(submissionId) {
              navigate(generatePath(APPLICATION_ROUTES.workspaceIntakeSubmission, { submissionId }));
            }}
          />
          <ListPagination
            page={page}
            pageSize={WORKSPACE_INTAKE_PAGE_SIZE}
            totalItems={submissionsPage.totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
